/**
 * predictiveAlert.js — PREDICTIVE-ALERT-ENGINE-1
 *
 * Zod schemas for:
 *   load_prediction_state    — per-in-transit-load prediction + margin state
 *   carrier_behavior_profile — per-carrier × per-lane × per-weekday OTP history
 *
 * Patent gate: F-PREDICTIVE-EXCEPTION-MARGIN-001
 * No public IDM language in this file.
 *
 * All Firestore access via DataService. EventBus via EventBusService.
 */
import { z } from 'zod';
import { TenantIdSchema } from './_primitives.js';

// ── Enums ──────────────────────────────────────────────────────────────────────

export const SILENCE_ESCALATION_LEVEL = Object.freeze({
  NONE:           'none',
  SECOND_ATTEMPT: 'second_attempt',
  BROKER_ALERT:   'broker_alert',
  FRAUD_SIGNAL:   'fraud_signal',
});

export const SilenceEscalationLevelEnum = z.enum(
  Object.values(SILENCE_ESCALATION_LEVEL),
);

// ── Sub-schemas ────────────────────────────────────────────────────────────────

const PredictionInputsSchema = z.object({
  departureOffsetMinutes:  z.number(),          // actual departure vs. pickup window start
  carrierOtpOnLane:        z.number().min(0).max(1), // 0–1 from CarrierBehaviorProfileService
  weatherAlertOnRoute:     z.boolean(),
  weatherAlertSeverity:    z.string().nullable(),
  trafficDelayMinutes:     z.number().nullable(),
  dwellAtShipperMinutes:   z.number().nullable(),
  lastGpsPingAge:          z.number(),          // minutes since last GPS ping
});

const MarginStateSchema = z.object({
  grossMarginDollars:          z.number(),      // locked at tender — load.financials.margin
  detentionThresholdMinutes:   z.number(),      // from tenant accessorial config
  detentionRatePerHourDollars: z.number(),      // from tenant accessorial config
  dwellMinutesElapsed:         z.number(),      // updated every GPS ping cycle
  detentionAtRiskDollars:      z.number(),      // cost if detention clock crosses threshold
  projectedMarginIfAtRisk:     z.number(),      // grossMarginDollars - detentionAtRiskDollars
  detentionClockActive:        z.boolean(),
});

const NotificationStateSchema = z.object({
  preEmptiveNotificationSent: z.boolean(),
  notificationSentAt:         z.string().nullable(),   // ISO timestamp
  rescheduledOffered:         z.boolean(),
  rescheduledAccepted:        z.boolean().nullable(),
});

const EscalationLogEntrySchema = z.object({
  level:       SilenceEscalationLevelEnum,
  triggeredAt: z.string(),                             // ISO timestamp
  action:      z.string(),
});

const SilenceStateSchema = z.object({
  lastCarrierResponseAt: z.string().nullable(),        // ISO timestamp
  silenceMinutes:        z.number(),
  escalationLevel:       SilenceEscalationLevelEnum,
  escalationLog:         z.array(EscalationLogEntrySchema),
});

const PredictionLogEntrySchema = z.object({
  scoredAt:         z.string(),                        // ISO timestamp
  probabilityScore: z.number().min(0).max(1),
  inputs:           z.record(z.string(), z.any()),     // snapshot of inputs at that cycle
});

// ── load_prediction_state ──────────────────────────────────────────────────────

export const LoadPredictionStateSchema = z.object({
  tenantId:                        TenantIdSchema,     // REQUIRED — first field
  loadId:                          z.string().min(1),
  carrierId:                       z.string().min(1),
  currentEta:                      z.string().nullable(),   // ISO timestamp
  deliveryWindowEnd:               z.string().nullable(),   // ISO timestamp
  probabilityScore:                z.number().min(0).max(1),
  probabilityThresholdCrossedAt:   z.string().nullable(),   // ISO timestamp — first crossing > 0.60

  inputs:            PredictionInputsSchema,
  marginState:       MarginStateSchema,
  notificationState: NotificationStateSchema,
  silenceState:      SilenceStateSchema,

  predictionLog:     z.array(PredictionLogEntrySchema), // append-only — every scoring cycle

  // Standard DataService fields
  isDeleted:  z.boolean().optional(),
  createdAt:  z.any().optional(),
  updatedAt:  z.any().optional(),
  version:    z.number().optional(),
}).passthrough();

// ── carrier_behavior_profile ───────────────────────────────────────────────────

const OtpHistoryEntrySchema = z.object({
  loadId:            z.string(),
  scheduledDelivery: z.string(),   // ISO timestamp
  actualDelivery:    z.string(),   // ISO timestamp
  onTime:            z.boolean(),
  minutesVariance:   z.number(),   // negative = early, positive = late
});

export const CarrierBehaviorProfileSchema = z.object({
  tenantId:   TenantIdSchema,
  carrierId:  z.string().min(1),
  laneKey:    z.string().min(1),      // `${originState}_${destState}`
  weekday:    z.number().int().min(0).max(6),  // 0=Sun … 6=Sat
  otpHistory: z.array(OtpHistoryEntrySchema),
  otpRate:    z.number().min(0).max(1),        // rolling 90d — recomputed on each append
  avgLateMinutes: z.number(),                  // avg when late
  sampleSize:     z.number().int().min(0),

  // Standard DataService fields
  isDeleted:  z.boolean().optional(),
  updatedAt:  z.any().optional(),
  version:    z.number().optional(),
}).passthrough();
